import React, { createContext, useCallback, useContext, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

/**
 * Generic confirmation dialog — replaces window.confirm() throughout the app.
 *
 * Usage:
 *   const confirm = useConfirm();
 *   const ok = await confirm('Are you sure?');
 *
 *   // With full options:
 *   const ok = await confirm({
 *     title: 'Cancel order',
 *     message: 'Do you want to request cancellation for this order?',
 *     confirmLabel: 'Yes, cancel',
 *     cancelLabel: 'Keep order',
 *     variant: 'danger',   // 'danger' | 'primary' | 'warning'  (default: 'primary')
 *   });
 */

const ConfirmDialogContext = createContext(null);

const DEFAULT_OPTIONS = {
  title: 'Are you sure?',
  message: '',
  confirmLabel: 'Confirm',
  cancelLabel: 'Cancel',
  variant: 'primary',
};

function ConfirmDialogModal({ open, options, onConfirm, onCancel }) {
  if (!open) return null;

  const { title, message, confirmLabel, cancelLabel, variant } = {
    ...DEFAULT_OPTIONS,
    ...options,
  };

  const btnClass =
    variant === 'danger'
      ? 'btn-danger'
      : variant === 'warning'
        ? 'btn-warning'
        : 'btn-primary';

  return createPortal(
    <>
      <div
        className="modal fade show d-block"
        role="dialog"
        aria-modal="true"
        aria-labelledby="confirm-dialog-title"
        tabIndex={-1}
        style={{ zIndex: 1055 }}
      >
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content shadow">
            <div className="modal-header border-0 pb-1">
              <h2 className="modal-title h6 fw-semibold" id="confirm-dialog-title">
                {title}
              </h2>
              <button
                type="button"
                className="btn-close"
                aria-label="Close"
                onClick={onCancel}
              />
            </div>
            {message ? (
              <div className="modal-body py-2">
                <p className="mb-0 small">{message}</p>
              </div>
            ) : null}
            <div className="modal-footer border-0 pt-1 gap-2">
              {cancelLabel ? (
                <button
                  type="button"
                  className="btn btn-outline-secondary btn-sm rounded-pill"
                  onClick={onCancel}
                >
                  {cancelLabel}
                </button>
              ) : null}
              <button
                type="button"
                className={`btn ${btnClass} btn-sm rounded-pill`}
                onClick={onConfirm}
                autoFocus
              >
                {confirmLabel}
              </button>
            </div>
          </div>
        </div>
      </div>
      <div className="modal-backdrop fade show" style={{ zIndex: 1054 }} />
    </>,
    document.body
  );
}

export function ConfirmDialogProvider({ children }) {
  const [state, setState] = useState({ open: false, options: {} });
  const resolveRef = useRef(null);

  const confirm = useCallback((optionsOrMessage) => {
    const options =
      typeof optionsOrMessage === 'string'
        ? { message: optionsOrMessage }
        : optionsOrMessage || {};

    return new Promise((resolve) => {
      resolveRef.current = resolve;
      setState({ open: true, options });
    });
  }, []);

  const handleConfirm = useCallback(() => {
    setState({ open: false, options: {} });
    resolveRef.current?.(true);
    resolveRef.current = null;
  }, []);

  const handleCancel = useCallback(() => {
    setState({ open: false, options: {} });
    resolveRef.current?.(false);
    resolveRef.current = null;
  }, []);

  return (
    <ConfirmDialogContext.Provider value={confirm}>
      {children}
      <ConfirmDialogModal
        open={state.open}
        options={state.options}
        onConfirm={handleConfirm}
        onCancel={handleCancel}
      />
    </ConfirmDialogContext.Provider>
  );
}

/** Returns an async confirm() function. Must be inside <ConfirmDialogProvider>. */
export function useConfirm() {
  const ctx = useContext(ConfirmDialogContext);
  if (!ctx) throw new Error('useConfirm must be used within a ConfirmDialogProvider');
  return ctx;
}
